import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';

import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
    Divider, Drawer, FormControl, FormControlLabel, IconButton, InputLabel,
    MenuItem, Pagination, Paper, Select, Stack, Switch, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, ToggleButton,
    ToggleButtonGroup, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
    getAiOperationObservability,
    getScoringObservability,
    getScoringPracticeDetail,
    type AiOperationObservability,
    type ScoringObservationItem,
    type ScoringObservabilityData,
    type ScoringPracticeDetail,
    type ScoringScoreRange,
    type ScoringTaskType,
} from '../admin/adminScoringObservabilityApi';

const taskOptions: Array<{ value: ScoringTaskType; label: string }> = [
    { value: 'DescribeImage', label: 'Describe Image' },
    { value: 'RetellLecture', label: 'Re-tell Lecture' },
    { value: 'SummarizeGroupDiscussion', label: 'Summarize Group Discussion' },
    { value: 'RespondToSituation', label: 'Respond to a Situation' },
    { value: 'SummarizeWrittenText', label: 'Summarize Written Text' },
    { value: 'SummarizeSpokenText', label: 'Summarize Spoken Text' },
    { value: 'WriteEssay', label: 'Write Essay' },
    { value: 'WriteEmail', label: 'Write Email' },
];

const windows = [
    { label: '1h', hours: 1 }, { label: '6h', hours: 6 }, { label: '24h', hours: 24 },
    { label: '7d', hours: 168 }, { label: '30d', hours: 720 },
];

const formatDateTime = (value: string) => new Intl.DateTimeFormat(undefined, {
    month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
}).format(new Date(value));
const formatDuration = (value: number) => value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${Math.round(value)}ms`;
const formatNumber = (value: number) => new Intl.NumberFormat().format(Math.round(value));
const percentage = (part: number, total: number) => total ? `${((part / total) * 100).toFixed(1)}%` : '0%';

function StatusChip({ status }: { status: string }) {
    const color = status === 'Completed' ? 'success' : status === 'Cancelled' ? 'warning' : 'error';
    return <Chip size="small" label={status} color={color} variant="outlined" />;
}

function Kpi({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
    return <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase' }}>{label}</Typography>
        <Typography sx={{ mt: 0.4, color: '#0f172a', fontSize: '1.35rem', fontWeight: 850 }}>{value}</Typography>
        {hint ? <Typography sx={{ mt: 0.25, color: '#94a3b8', fontSize: '0.75rem' }}>{hint}</Typography> : null}
    </Paper>;
}

type ScoreBucketSelection = {
    examTaskType: ScoringTaskType;
    displayName: string;
    examName: ScoringScoreRange['examName'];
    traitName: string;
    score: number;
    maximumScore: number;
};

function TaskCard({
    item,
    onExplore,
}: {
    item: ScoringObservabilityData['taskTypes'][number];
    onExplore: (selection: ScoreBucketSelection) => void;
}) {
    return <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
        <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{item.displayName}</Typography>
        <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>{item.examTaskType}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mt: 1.25 }}>
            <Kpi label="Runs" value={item.totalCount} /><Kpi label="Failed" value={item.failedCount} />
            <Kpi label="Fallback" value={item.fallbackCount} /><Kpi label="P95" value={formatDuration(item.p95DurationMs)} />
        </Box>
        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
            {item.scoreRanges.map((range) => <Box key={`${range.examName}-${range.maximumScore}`}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" label={range.examName} variant="outlined" />
                    <Typography sx={{ color: '#475569', fontSize: '0.75rem', fontWeight: 800 }}>
                        {range.traitName} 0–{range.maximumScore}
                    </Typography>
                </Stack>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" aria-label={`${range.examName} ${range.traitName} score buckets`}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ py: 0.6, px: 1, fontSize: '0.7rem', fontWeight: 800 }}>Score</TableCell>
                                <TableCell align="right" sx={{ py: 0.6, px: 1, fontSize: '0.7rem', fontWeight: 800 }}>Practices</TableCell>
                                <TableCell align="right" sx={{ py: 0.6, px: 1, fontSize: '0.7rem', fontWeight: 800 }}>Detail</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {range.buckets.map((bucket) => <TableRow key={bucket.score}>
                                <TableCell sx={{ py: 0.35, px: 1, fontWeight: 800 }}>{bucket.score}</TableCell>
                                <TableCell align="right" sx={{ py: 0.35, px: 1 }}>{formatNumber(bucket.practiceCount)}</TableCell>
                                <TableCell align="right" sx={{ py: 0.2, px: 0.5 }}>
                                    <Button
                                        size="small"
                                        disabled={bucket.practiceCount === 0}
                                        onClick={() => onExplore({
                                            examTaskType: item.examTaskType,
                                            displayName: item.displayName,
                                            examName: range.examName,
                                            traitName: range.traitName,
                                            score: bucket.score,
                                            maximumScore: range.maximumScore,
                                        })}
                                        sx={{ minWidth: 0, px: 0.75, fontSize: '0.68rem', textTransform: 'none' }}
                                    >
                                        Explore
                                    </Button>
                                </TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>)}
            {!item.scoreRanges.length ? <Typography sx={{ color: '#94a3b8', fontSize: '0.76rem' }}>No configured Content score range.</Typography> : null}
        </Stack>
    </Paper>;
}

function ObservationCard({ item, onOpen }: { item: ScoringObservationItem; onOpen: () => void }) {
    return <Paper variant="outlined" onClick={onOpen} sx={{ p: 1.5, borderRadius: 1, cursor: 'pointer' }}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800 }}>{taskOptions.find((option) => option.value === item.examTaskType)?.label ?? item.examTaskType}</Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>{formatDateTime(item.createdAtUtc)} · {item.scoringMode}</Typography>
            </Box><StatusChip status={item.status} />
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 1.2 }}>
            <Typography sx={{ fontSize: '0.82rem' }}><strong>{formatDuration(item.durationMs)}</strong> duration</Typography>
            <Typography sx={{ fontSize: '0.82rem' }}><strong>{item.totalTokens}</strong> tokens</Typography>
            {item.fallbackUsed ? <Chip size="small" label="Fallback" color="warning" /> : null}
        </Stack>
        {item.issueCode ? <Alert severity="error" sx={{ mt: 1.2, py: 0 }}>{item.issueCode}</Alert> : null}
    </Paper>;
}

function DetailDrawer({ item, onClose }: { item: ScoringObservationItem | null; onClose: () => void }) {
    return <Drawer anchor="right" open={Boolean(item)} onClose={onClose}>
        <Box sx={{ width: { xs: '100vw', sm: 520 }, maxWidth: '100vw', p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 850 }}>Scoring run details</Typography>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Stack>
            {item ? <Stack spacing={2} sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <StatusChip status={item.status} /><Chip size="small" label={item.scoringMode} /><Chip size="small" label={item.examName} />
                    {item.fallbackUsed ? <Chip size="small" color="warning" label="Fallback used" /> : null}
                </Stack>
                <Box><Typography sx={{ fontWeight: 800 }}>{taskOptions.find((option) => option.value === item.examTaskType)?.label}</Typography><Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>{item.questionNo || 'No question number'} · {formatDateTime(item.createdAtUtc)}</Typography></Box>
                {item.issueCode ? <Alert severity="error"><strong>{item.issueCode}</strong>{item.issueMessage ? ` — ${item.issueMessage}` : ''}</Alert> : null}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                    <Kpi label="Duration" value={formatDuration(item.durationMs)} /><Kpi label="Score" value={item.score == null ? '—' : `${item.score}/${item.maxScore ?? '—'}`} />
                    <Kpi label="Words" value={item.responseWordCount ?? '—'} /><Kpi label="Audio" value={item.audioDurationSeconds == null ? '—' : `${item.audioDurationSeconds.toFixed(1)}s`} />
                    <Kpi label="Model attempts" value={item.modelAttemptCount} /><Kpi label="Tokens" value={formatNumber(item.totalTokens)} hint={`${item.promptTokens} input · ${item.completionTokens} output`} />
                </Box>
                <Divider /><Typography sx={{ fontWeight: 850 }}>Model attempts</Typography>
                {item.modelAttempts.length ? item.modelAttempts.map((attempt, index) => <Paper key={`${attempt.role}-${index}`} variant="outlined" sx={{ p: 1.4, borderRadius: 1 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Box><Typography sx={{ fontWeight: 800 }}>{attempt.role}: {attempt.model}</Typography><Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>{formatDuration(attempt.durationMs)} · {attempt.totalTokens} tokens</Typography></Box>
                        <StatusChip status={attempt.status} />
                    </Stack>{attempt.failureCategory ? <Typography sx={{ mt: 0.8, color: '#b91c1c', fontSize: '0.82rem' }}>{attempt.failureCategory}</Typography> : null}
                </Paper>) : <Typography sx={{ color: '#64748b' }}>No model call was used for this scoring run.</Typography>}
                <Divider /><Stack spacing={0.5}>
                    <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>Observation ID: {item.scoringObservationId}</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>Request ID: {item.requestId || '—'}</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>Trace ID: {item.traceId || '—'}</Typography>
                </Stack>
            </Stack> : null}
        </Box>
    </Drawer>;
}

function ScoreBucketExplorer({
    selection,
    range,
    fallbackOnly,
    issueOnly,
    onClose,
}: {
    selection: ScoreBucketSelection | null;
    range: { fromUtc: string; toUtc: string };
    fallbackOnly: boolean;
    issueOnly: boolean;
    onClose: () => void;
}) {
    const [page, setPage] = useState(1);
    const [runs, setRuns] = useState<ScoringObservabilityData | null>(null);
    const [detail, setDetail] = useState<ScoringPracticeDetail | null>(null);
    const [selectedObservationId, setSelectedObservationId] = useState<string | null>(null);
    const [loadingRuns, setLoadingRuns] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!selection) return;
        let active = true;
        void getScoringObservability({
            ...range,
            examName: selection.examName,
            examTaskType: selection.examTaskType,
            status: 'Completed',
            scoringMode: 'Practice',
            contentScore: selection.score,
            contentMaxScore: selection.maximumScore,
            fallbackOnly,
            issueOnly,
            page,
            pageSize: 25,
        }).then((result) => {
            if (!active) return;
            setRuns(result);
            const first = result.items[0]?.scoringObservationId ?? null;
            setSelectedObservationId(first);
            setDetail(null);
            setLoadingDetail(Boolean(first));
        }).catch((reason) => {
            if (active) setError(reason instanceof Error ? reason.message : 'Could not load score-bucket practices.');
        }).finally(() => {
            if (active) setLoadingRuns(false);
        });
        return () => { active = false; };
    }, [fallbackOnly, issueOnly, page, range, selection]);

    useEffect(() => {
        if (!selectedObservationId) return;
        let active = true;
        void getScoringPracticeDetail(selectedObservationId).then((result) => {
            if (active) setDetail(result);
        }).catch((reason) => {
            if (active) setError(reason instanceof Error ? reason.message : 'Could not load the practice details.');
        }).finally(() => {
            if (active) setLoadingDetail(false);
        });
        return () => { active = false; };
    }, [selectedObservationId]);

    const markRows = Array.isArray(detail?.systemResponse?.markTableResult)
        ? detail.systemResponse.markTableResult.filter((value): value is Record<string, unknown> => Boolean(value) && typeof value === 'object')
        : [];
    const totalPages = Math.max(1, Math.ceil((runs?.totalRows ?? 0) / (runs?.pageSize ?? 25)));

    return <Dialog open={Boolean(selection)} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pr: 7 }}>
            <Typography sx={{ fontWeight: 850 }}>
                {selection ? `${selection.displayName}: ${selection.traitName} ${selection.score}/${selection.maximumScore}` : 'Practice score details'}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                {selection?.examName} · completed practice attempts in the selected dashboard window
            </Typography>
            <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.25, md: 2 } }}>
            {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px minmax(0, 1fr)' }, gap: 1.5 }}>
                <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <Box sx={{ p: 1.25, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontWeight: 800 }}>Practices</Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                            {formatNumber(runs?.totalRows ?? 0)} matching attempts
                        </Typography>
                    </Box>
                    {loadingRuns ? <Box sx={{ py: 5, display: 'grid', placeItems: 'center' }}><CircularProgress size={24} /></Box> : <Stack sx={{ maxHeight: 520, overflowY: 'auto' }}>
                        {runs?.items.map((item) => {
                            const active = item.scoringObservationId === selectedObservationId;
                            return <Button
                                key={item.scoringObservationId}
                                onClick={() => {
                                    setSelectedObservationId(item.scoringObservationId);
                                    setDetail(null);
                                    setLoadingDetail(true);
                                    setError(null);
                                }}
                                sx={{
                                    display: 'block',
                                    borderRadius: 0,
                                    px: 1.25,
                                    py: 1,
                                    textAlign: 'left',
                                    textTransform: 'none',
                                    color: '#0f172a',
                                    backgroundColor: active ? alpha('#2563eb', 0.09) : 'transparent',
                                    borderBottom: '1px solid #f1f5f9',
                                }}
                            >
                                <Typography sx={{ fontWeight: 800, fontSize: '0.8rem' }}>{item.questionNo || 'Practice response'}</Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>{formatDateTime(item.createdAtUtc)}</Typography>
                                <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem', overflowWrap: 'anywhere' }}>{item.userId || 'Unknown user'}</Typography>
                            </Button>;
                        })}
                        {!runs?.items.length ? <Typography sx={{ p: 2, color: '#64748b', fontSize: '0.8rem' }}>No persisted practice details in this bucket.</Typography> : null}
                    </Stack>}
                    {totalPages > 1 ? <Pagination
                        size="small"
                        page={page}
                        count={totalPages}
                        onChange={(_, value) => {
                            setPage(value);
                            setRuns(null);
                            setDetail(null);
                            setSelectedObservationId(null);
                            setLoadingRuns(true);
                            setError(null);
                        }}
                        sx={{ p: 1, '& .MuiPagination-ul': { justifyContent: 'center' } }}
                    /> : null}
                </Paper>

                <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.75 }, minWidth: 0 }}>
                    {loadingDetail ? <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box> : detail ? <Stack spacing={1.5}>
                        <Box>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                <Chip size="small" label={detail.examName} />
                                <Chip size="small" variant="outlined" label={detail.questionNo || detail.examTaskType} />
                            </Stack>
                            <Typography sx={{ mt: 0.8, fontWeight: 850 }}>
                                {[detail.firstName, detail.lastName].filter(Boolean).join(' ') || 'Unnamed user'}
                            </Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.78rem', overflowWrap: 'anywhere' }}>
                                {detail.email || detail.userId} · {formatDateTime(detail.practicedAtUtc)}
                            </Typography>
                        </Box>

                        <Divider />
                        <Box>
                            <Typography sx={{ mb: 0.6, fontWeight: 850 }}>Learner response</Typography>
                            <Paper variant="outlined" sx={{ p: 1.25, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: '0.82rem', backgroundColor: '#f8fafc' }}>
                                {detail.learnerResponse || 'No text or transcript was persisted.'}
                            </Paper>
                        </Box>

                        <Box>
                            <Typography sx={{ mb: 0.6, fontWeight: 850 }}>Question source</Typography>
                            <Paper variant="outlined" sx={{ p: 1.25, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: '0.82rem', backgroundColor: '#f8fafc' }}>
                                {detail.promptBody || 'No prompt body was persisted.'}
                            </Paper>
                        </Box>

                        <Box>
                            <Typography sx={{ mb: 0.6, fontWeight: 850 }}>Awarded scores</Typography>
                            {markRows.length ? <TableContainer component={Paper} variant="outlined"><Table size="small">
                                <TableHead><TableRow><TableCell>Trait</TableCell><TableCell align="right">Score</TableCell></TableRow></TableHead>
                                <TableBody>{markRows.map((row, index) => <TableRow key={`${String(row.title)}-${index}`}>
                                    <TableCell>{String(row.title ?? 'Unknown')}</TableCell>
                                    <TableCell align="right">{String(row.score ?? '—')} / {String(row.outOf ?? '—')}</TableCell>
                                </TableRow>)}</TableBody>
                            </Table></TableContainer> : <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>No score breakdown was persisted.</Typography>}
                        </Box>

                        <Box>
                            <Typography sx={{ mb: 0.6, fontWeight: 850 }}>Processed system response</Typography>
                            <Paper
                                component="pre"
                                variant="outlined"
                                sx={{
                                    m: 0,
                                    p: 1.25,
                                    maxHeight: 360,
                                    overflow: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
                                    fontSize: '0.72rem',
                                    backgroundColor: '#0f172a',
                                    color: '#e2e8f0',
                                }}
                            >
                                {JSON.stringify(detail.systemResponse, null, 2)}
                            </Paper>
                        </Box>
                    </Stack> : <Typography sx={{ py: 8, textAlign: 'center', color: '#64748b' }}>Select a practice attempt to inspect its response and scoring result.</Typography>}
                </Paper>
            </Box>
        </DialogContent>
    </Dialog>;
}

export function ScoringObservabilityPage() {
    const [windowHours, setWindowHours] = useState(24);
    const [taskType, setTaskType] = useState(''); const [status, setStatus] = useState(''); const [mode, setMode] = useState('');
    const [fallbackOnly, setFallbackOnly] = useState(false); const [issueOnly, setIssueOnly] = useState(false); const [page, setPage] = useState(1);
    const [data, setData] = useState<ScoringObservabilityData | null>(null); const [selected, setSelected] = useState<ScoringObservationItem | null>(null);
    const [scoreBucket, setScoreBucket] = useState<ScoreBucketSelection | null>(null);
    const [aiOperations, setAiOperations] = useState<AiOperationObservability | null>(null);
    const [loading, setLoading] = useState(false); const [error, setError] = useState<string | null>(null);
    const range = useMemo(() => { const to = new Date(); return { toUtc: to.toISOString(), fromUtc: new Date(to.getTime() - windowHours * 3600000).toISOString() }; }, [windowHours]);
    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const [runs, operations] = await Promise.all([
                getScoringObservability({ ...range, examTaskType: taskType, status, scoringMode: mode, fallbackOnly, issueOnly, page, pageSize: 25 }),
                getAiOperationObservability({ ...range, examTaskType: taskType }),
            ]);
            setData(runs); setAiOperations(operations);
        }
        catch (reason) { setError(reason instanceof Error ? reason.message : 'Scoring observability failed'); }
        finally { setLoading(false); }
    }, [fallbackOnly, issueOnly, mode, page, range, status, taskType]);
    useEffect(() => { void load(); }, [load]);
    const changeFilter = <T,>(setter: (value: T) => void, value: T) => { setter(value); setPage(1); };
    const maxTimelineTotal = Math.max(1, ...(data?.timeline.map((bucket) => bucket.totalCount) ?? [1]));

    return <Box sx={{ p: { xs: 1.25, sm: 2, md: 3 }, backgroundColor: '#f8fafc', minHeight: '100%' }}>
        <Title title="Scoring Observability" />
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
            <Box><Typography variant="h4" sx={{ fontWeight: 850, fontSize: { xs: '1.45rem', md: '2.1rem' } }}>Scoring Observability</Typography><Typography sx={{ color: '#64748b' }}>Score latency, deferred feedback, failures, model usage, and fallback facts for all semantic task types.</Typography></Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><ToggleButtonGroup exclusive size="small" value={windowHours} onChange={(_, value) => value && changeFilter(setWindowHours, value)}>{windows.map((item) => <ToggleButton key={item.hours} value={item.hours}>{item.label}</ToggleButton>)}</ToggleButtonGroup><Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />} onClick={() => void load()} disabled={loading}>Refresh</Button></Stack>
        </Stack>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
                <FormControl size="small"><InputLabel>Task type</InputLabel><Select label="Task type" value={taskType} onChange={(event) => changeFilter(setTaskType, event.target.value)}><MenuItem value="">All semantic types</MenuItem>{taskOptions.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}</Select></FormControl>
                <FormControl size="small"><InputLabel>Status</InputLabel><Select label="Status" value={status} onChange={(event) => changeFilter(setStatus, event.target.value)}><MenuItem value="">All statuses</MenuItem><MenuItem value="Completed">Completed</MenuItem><MenuItem value="Failed">Failed</MenuItem><MenuItem value="Cancelled">Cancelled</MenuItem></Select></FormControl>
                <FormControl size="small"><InputLabel>Mode</InputLabel><Select label="Mode" value={mode} onChange={(event) => changeFilter(setMode, event.target.value)}><MenuItem value="">All modes</MenuItem><MenuItem value="Practice">Practice</MenuItem><MenuItem value="MockTest">Mock test</MenuItem><MenuItem value="Direct">Direct</MenuItem><MenuItem value="Unknown">Unknown</MenuItem></Select></FormControl>
            </Box><Stack direction={{ xs: 'column', sm: 'row' }} sx={{ mt: 1 }}><FormControlLabel control={<Switch checked={issueOnly} onChange={(event) => changeFilter(setIssueOnly, event.target.checked)} />} label="Issues only" /><FormControlLabel control={<Switch checked={fallbackOnly} onChange={(event) => changeFilter(setFallbackOnly, event.target.checked)} />} label="Fallback used only" /></Stack>
        </Paper>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' }, gap: 1.25, mb: 2 }}>
            <Kpi label="Runs" value={data?.summary.totalCount ?? 0} /><Kpi label="Failure rate" value={percentage(data?.summary.failedCount ?? 0, data?.summary.totalCount ?? 0)} hint={`${data?.summary.failedCount ?? 0} failed`} /><Kpi label="Fallback rate" value={percentage(data?.summary.fallbackCount ?? 0, data?.summary.totalCount ?? 0)} hint={`${data?.summary.fallbackCount ?? 0} runs`} /><Kpi label="P95 duration" value={formatDuration(data?.summary.p95DurationMs ?? 0)} hint={`P50 ${formatDuration(data?.summary.p50DurationMs ?? 0)}`} /><Kpi label="Total tokens" value={formatNumber(data?.summary.totalTokens ?? 0)} hint={`${formatNumber(data?.summary.promptTokens ?? 0)} input`} />
        </Box>
        <Typography sx={{ mb: 1, fontWeight: 850 }}>AI operation split</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25, mb: 2 }}>
            {(aiOperations?.models ?? []).filter((item) => item.operation === 'SemanticDetailedFeedback' || item.operation.includes('Semantic')).map((item) => <Paper key={`${item.provider}-${item.model}-${item.operation}`} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                <Stack direction="row" justifyContent="space-between" spacing={1}><Box><Typography sx={{ fontWeight: 800 }}>{item.operation === 'SemanticDetailedFeedback' ? 'Deferred feedback' : 'Live score decision'}</Typography><Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>{item.operation} · {item.model}</Typography></Box><Chip size="small" color={item.failedInvocations ? 'warning' : 'success'} label={`${item.completedInvocations}/${item.totalInvocations} done`} /></Stack>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}><Typography sx={{ fontSize: '0.82rem' }}><strong>{formatDuration(item.p95DurationMs)}</strong> P95</Typography><Typography sx={{ fontSize: '0.82rem' }}><strong>{formatNumber(item.totalTokens)}</strong> tokens</Typography></Stack>
            </Paper>)}
            {!aiOperations?.models.some((item) => item.operation === 'SemanticDetailedFeedback' || item.operation.includes('Semantic')) ? <Paper variant="outlined" sx={{ p: 2, color: '#64748b' }}>No semantic model operations in this window.</Paper> : null}
        </Box>
        <Typography sx={{ mb: 1, fontWeight: 850 }}>Task breakdown</Typography><Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25, mb: 2 }}>{data?.taskTypes.map((item) => <TaskCard key={item.examTaskType} item={item} onExplore={setScoreBucket} />)}</Box>
        <Paper variant="outlined" sx={{ p: 1.75, mb: 2, borderRadius: 1 }}><Typography sx={{ fontWeight: 850 }}>Run volume timeline</Typography><Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>Red marks failures; amber marks fallback use.</Typography><Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 130, mt: 1.5, overflowX: 'auto' }}>{data?.timeline.map((bucket) => <Box key={bucket.bucketStartUtc} title={`${formatDateTime(bucket.bucketStartUtc)}: ${bucket.totalCount} runs, ${bucket.failedCount} failed, ${bucket.fallbackCount} fallback`} sx={{ minWidth: 18, flex: 1, maxWidth: 54, height: `${Math.max(6, (bucket.totalCount / maxTimelineTotal) * 100)}%`, background: bucket.failedCount ? '#ef4444' : bucket.fallbackCount ? '#f59e0b' : '#2563eb', borderRadius: '3px 3px 0 0' }} />)}{!data?.timeline.length ? <Typography sx={{ color: '#94a3b8', alignSelf: 'center' }}>No runs in this window.</Typography> : null}</Box></Paper>
        <Typography sx={{ mb: 1, fontWeight: 850 }}>Recent scoring runs</Typography><Stack spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>{data?.items.map((item) => <ObservationCard key={item.scoringObservationId} item={item} onOpen={() => setSelected(item)} />)}</Stack>
        <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 1 }}><Table size="small"><TableHead><TableRow><TableCell>Started</TableCell><TableCell>Task</TableCell><TableCell>Status</TableCell><TableCell>Mode</TableCell><TableCell align="right">Duration</TableCell><TableCell>Model</TableCell><TableCell align="right">Tokens</TableCell><TableCell>Issue</TableCell></TableRow></TableHead><TableBody>{data?.items.map((item) => <TableRow hover key={item.scoringObservationId} onClick={() => setSelected(item)} sx={{ cursor: 'pointer', backgroundColor: item.status === 'Failed' ? alpha('#ef4444', 0.035) : 'inherit' }}><TableCell>{formatDateTime(item.createdAtUtc)}</TableCell><TableCell><Typography sx={{ fontWeight: 750 }}>{taskOptions.find((option) => option.value === item.examTaskType)?.label}</Typography><Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>{item.questionNo || item.examTaskType}</Typography></TableCell><TableCell><StatusChip status={item.status} /></TableCell><TableCell>{item.scoringMode}</TableCell><TableCell align="right">{formatDuration(item.durationMs)}</TableCell><TableCell>{item.modelUsed || 'No model'}{item.fallbackUsed ? <Chip size="small" label="Fallback" color="warning" sx={{ ml: 0.75 }} /> : null}</TableCell><TableCell align="right">{item.totalTokens}</TableCell><TableCell sx={{ color: item.issueCode ? '#b91c1c' : '#64748b' }}>{item.issueCode || '—'}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
        {!loading && !data?.items.length ? <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: '#64748b' }}>No scoring observations match these filters.</Paper> : null}
        {(data?.totalRows ?? 0) > (data?.pageSize ?? 25) ? <Stack alignItems="center" sx={{ mt: 2 }}><Pagination page={page} count={Math.ceil((data?.totalRows ?? 0) / (data?.pageSize ?? 25))} onChange={(_, value) => setPage(value)} /></Stack> : null}
        <DetailDrawer item={selected} onClose={() => setSelected(null)} />
        <ScoreBucketExplorer
            key={scoreBucket ? `${scoreBucket.examName}-${scoreBucket.examTaskType}-${scoreBucket.score}-${scoreBucket.maximumScore}` : 'closed'}
            selection={scoreBucket}
            range={range}
            fallbackOnly={fallbackOnly}
            issueOnly={issueOnly}
            onClose={() => setScoreBucket(null)}
        />
    </Box>;
}
